// hooks/CaseView/useFiles.ts
import { useCallback, useState } from "react";
import { useStore } from "../../context/StoreContext";
import { useToast } from "../../context/ToastContext";
import { Endpoint } from "../../api/base";

export const useFiles = (caseId: string) => {
  const { files, setFiles, UploadFilesContext } = useStore();
  const { showToast } = useToast();
  const [isDeleting, setIsDeleting] = useState(false);

  const fetchFiles = useCallback(async () => {
    try {
      // if (!uploadLoading.isActive) return;
      const res = await Endpoint.getFiles({ key: caseId });
      setFiles(res.data);
    } catch (err) {
      console.error("Error fetching files:", err);
      showToast("Failed to load files", "error");
    }
  }, [caseId, setFiles, showToast]);

  const deleteFile = useCallback(
    async (fileKey: string) => {
      setIsDeleting(true);

      try {
        await Endpoint.deleteFile(fileKey);

        // Immutable update - filter out deleted file
        setFiles((prevFiles) =>
          prevFiles.filter((file) => file.key !== fileKey)
        );

        showToast("File deleted successfully", "success");
        return true;
      } catch (err) {
        console.error("Error deleting file:", err);
        showToast("Failed to delete file", "error");
        return false;
      } finally {
        setIsDeleting(false);
      }
    },
    [setFiles, showToast]
  );

  const uploadFiles = useCallback(
    async (fileList: File[]) => {
      if (fileList.length === 0) return;

      try {
        // Use batch upload - gets all signed URLs in one API call
        const uploadedKeys = await UploadFilesContext(fileList, caseId);

        if (uploadedKeys && uploadedKeys.length > 0) {
          // Refresh file list after successful uploads
          await fetchFiles();
        }
      } catch (error) {
        console.error("Error uploading files:", error);
        showToast("Some files failed to upload", "error");
      }
    },
    [caseId, UploadFilesContext, fetchFiles, showToast]
  );

  return {
    files,
    isDeleting,
    fetchFiles,
    deleteFile,
    uploadFiles,
  };
};
